import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../context/AuthContext';

// Schéma de validation
const validationSchema = Yup.object({
  email: Yup.string()
    .email('Adresse email invalide')
    .required('L\'email est requis'),
  password: Yup.string()
    .required('Le mot de passe est requis')
});

// Clés pour le localStorage
const REMEMBER_ME_KEY = 'remember_me';
const SAVED_CREDENTIALS_KEY = 'saved_credentials';

const LoginForm = () => {
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [initialValues, setInitialValues] = useState({ email: '', password: '' });
  const formikRef = useRef();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Charger les informations sauvegardées au montage du composant
  useEffect(() => {
    const savedRememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
    const savedCredentials = localStorage.getItem(SAVED_CREDENTIALS_KEY);
    
    setRememberMe(savedRememberMe);
    
    if (savedRememberMe && savedCredentials) {
      try {
        const credentials = JSON.parse(savedCredentials);
        setInitialValues({
          email: credentials.email || '',
          password: credentials.password || ''
        });
      } catch (error) {
        console.error('Erreur lors du chargement des informations sauvegardées:', error);
        localStorage.removeItem(SAVED_CREDENTIALS_KEY);
        localStorage.removeItem(REMEMBER_ME_KEY);
      }
    }
  }, []);
  
  // Fonction pour forcer la vidange des champs
  const clearFields = () => {
    if (formikRef.current) {
      formikRef.current.setFieldValue('email', '');
      formikRef.current.setFieldValue('password', '');
    }
    
    // Aussi vider directement les éléments DOM
    setTimeout(() => {
      const emailField = document.getElementById('email');
      const passwordField = document.getElementById('password');
      
      if (emailField) {
        emailField.value = '';
        emailField.setAttribute('value', '');
      }
      if (passwordField) {
        passwordField.value = '';
        passwordField.setAttribute('value', '');
      }
    }, 0);
  };
  
  // Surveiller le remplissage automatique et l'annuler si nécessaire
  useEffect(() => {
    if (!rememberMe) {
      // Vérifier périodiquement si le navigateur a rempli les champs
      const checkAndClear = () => {
        const emailField = document.getElementById('email');
        const passwordField = document.getElementById('password');
        
        if ((emailField && emailField.value) || (passwordField && passwordField.value)) {
          clearFields();
        }
      };
      
      // Vérifications multiples avec des délais différents
      const timeouts = [50, 100, 200, 500, 1000, 2000];
      const timers = timeouts.map(delay => 
        setTimeout(checkAndClear, delay)
      );
      
      return () => timers.forEach(timer => clearTimeout(timer));
    }
  }, [rememberMe]);
  
  // État supplémentaire pour maintenir la session
  const [maintainSession, setMaintainSessionState] = useState(
    localStorage.getItem('maintain_session') === 'true'
  );
  
  // Gérer le changement de "Maintenir la session"
  const handleMaintainSessionChange = (e) => {
    const checked = e.target.checked;
    setMaintainSessionState(checked);
    localStorage.setItem('maintain_session', checked.toString());
  };
  const handleRememberMeChange = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);
    
    localStorage.setItem(REMEMBER_ME_KEY, checked.toString());
    
    if (!checked) {
      localStorage.removeItem(SAVED_CREDENTIALS_KEY);
      setInitialValues({ email: '', password: '' });
      clearFields();
    }
  };
  
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError('');
      
      if (rememberMe) {
        localStorage.setItem(SAVED_CREDENTIALS_KEY, JSON.stringify({
          email: values.email,
          password: values.password
        }));
      }
      
      const result = await login(values);
      
      if (result.success) {
        const from = location.state?.from?.pathname || '/';
        navigate(from, { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
      console.error('Erreur de connexion:', err);
    } finally {
      setSubmitting(false);
    }
  };
    
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Connexion à EtatOnline
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Veuillez vous connecter pour accéder à l'application
          </p>
        </div>
        
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting, setFieldValue }) => (
            <Form className="mt-8 space-y-6">
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">{error}</div>
                </div>
              )}
              
              <div className="rounded-md shadow-sm -space-y-px">
                <div>
                  <label htmlFor="email" className="sr-only">
                    Adresse email
                  </label>
                  <Field
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="new-email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="login"
                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder="Adresse email"
                    onFocus={(e) => {
                      // Vider le champ au focus si c'est de l'autocomplétion non désirée
                      if (!rememberMe && e.target.value && !formikRef.current?.values.email) {
                        e.target.value = '';
                        formikRef.current?.setFieldValue('email', '');
                      }
                    }}
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="sr-only">
                    Mot de passe
                  </label>
                  <Field
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck="false"
                    data-lpignore="true"
                    data-form-type="login"
                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder="Mot de passe"
                    onFocus={(e) => {
                      // Vider le champ au focus si c'est de l'autocomplétion non désirée
                      if (!rememberMe && e.target.value && !formikRef.current?.values.password) {
                        e.target.value = '';
                        formikRef.current?.setFieldValue('password', '');
                      }
                    }}
                  />
                  <ErrorMessage
                    name="password"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
              </div>
              
              {/* Case à cocher "Rester connecté" */}
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Rester connecté (sauvegarder email et mot de passe)
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    id="maintain-session"
                    name="maintain-session"
                    type="checkbox"
                    checked={maintainSession}
                    onChange={handleMaintainSessionChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="maintain-session" className="ml-2 block text-sm text-gray-900">
                    Maintenir la session active (éviter la page de connexion)
                  </label>
                </div>
              </div>
              
              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Connexion en cours...' : 'Se connecter'}
                </button>
              </div>
              
              {/* Information de sécurité */}
              {rememberMe && (
                <div className="rounded-md bg-blue-50 p-4">
                  <div className="text-xs text-blue-700">
                    <strong>Attention :</strong> Vos informations de connexion seront sauvegardées 
                    sur cet appareil. N'utilisez cette option que sur des appareils personnels et bien sécurisés.
                  </div>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default LoginForm;